import { FC, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { Permission } from '@/api/permissions';
import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import useDebounce from '@/global-hooks/use-debounce';
import { Accordion, Input, Progress, Tabs, theme, Typography } from '@/shared/design-system';
import {
  CatalogGroup,
  CatalogPermission,
  PermissionAssignment,
} from '@/shared/modules/role-board/types';
import {
  countSelectedInGroup,
  filterCatalogGroups,
  isPermissionGrantable,
  PermissionSelectionMap,
} from '@/utils/permission-catalog';
import type { PermissionDraft } from '@/utils/permission-draft';
import type { PendingPermissionChange } from '@/utils/role-request-activity';

import { GroupBulkActions } from './group-bulk-actions';
import { PermissionRow } from './permission-row';
import {
  EmptyState,
  GroupCount,
  GroupLabel,
  GroupLabelMeta,
  GroupList,
  GroupMeter,
  PermissionRows,
  PickerRoot,
  PickerToolbar,
  SearchField,
} from './styled';

export const NO_MATCHES_MESSAGE = 'No permissions match that search';
export const NOTHING_GRANTED_MESSAGE = 'This role grants nothing yet';

export const PERMISSION_FILTERS = {
  all: 'all',
  granted: 'granted',
} as const;

export type PermissionFilter = (typeof PERMISSION_FILTERS)[keyof typeof PERMISSION_FILTERS];

export type PermissionPickerProps = {
  groups: readonly CatalogGroup[];
  /** What is currently on, and which rows are locked, for the surface being edited. */
  selection: PermissionSelectionMap;
  /** Whether the caller may grant role administration — see `PROTECTED_PERMISSIONS`. */
  canGrantProtected?: boolean;
  /** False when the caller may view the board but not change it. */
  editable: boolean;
  bulkAddBlockedGroups?: ReadonlySet<string>;
  onChange: (_permission: CatalogPermission, _granted: boolean) => void;
  /**
   * Enables the per-group "Enable all" / "Clear all" toolbar. Omitted on a surface with no
   * batch endpoint — the toolbar simply doesn't render.
   */
  onBulkChange?: (
    _group: CatalogGroup,
    _add: PermissionAssignment[],
    _remove: Permission[],
  ) => void;
  /** Rendered above the group list, e.g. a "12 of 231 selected" summary. */
  summary?: string;
  /** Rendered at the top of a group's body once it has been opened; return `null` for the rest. */
  renderGroupIntro?: (_group: CatalogGroup) => ReactNode;
  /** Staged, unsaved edits: their rows are marked so the reviewer sees what moved. */
  draft?: PermissionDraft;
  /** Requests already waiting for a decision: their rows are marked and frozen. */
  pending?: ReadonlyMap<Permission, PendingPermissionChange>;
  /**
   * Offers an "All / Granted" switch. A role is first read for what it grants, so a surface
   * showing an existing role starts on Granted; a picker building a new role starts on All.
   */
  defaultFilter?: PermissionFilter;
  showFilter?: boolean;
  /**
   * Groups that stay listed under Granted even when none of their rows is on, because their
   * intro carries a setting of its own (the address-book category scope).
   */
  pinnedGroups?: ReadonlySet<string>;
};

export const PermissionPicker: FC<PermissionPickerProps> = ({
  groups,
  selection,
  canGrantProtected = false,
  editable,
  bulkAddBlockedGroups,
  onChange,
  onBulkChange,
  summary,
  renderGroupIntro,
  draft,
  pending,
  defaultFilter = PERMISSION_FILTERS.all,
  showFilter = false,
  pinnedGroups,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<PermissionFilter>(defaultFilter);
  const debouncedSearch = useDebounce(search, 200);
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const grantedCount = useMemo(
    () => groups.reduce((total, group) => total + countSelectedInGroup(group, selection), 0),
    [groups, selection],
  );

  const visibleGroups = useMemo(() => {
    const searched = filterCatalogGroups(groups, debouncedSearch);
    if (!showFilter || filter === PERMISSION_FILTERS.all) return searched;

    // A row the draft is about to change stays in view under Granted, so switching it off does
    // not make it vanish before the change is reviewed.
    return searched.flatMap((group) => {
      const permissions = group.permissions.filter(
        ({ permission }) => selection.has(permission) || draft?.has(permission),
      );

      return permissions.length || pinnedGroups?.has(group.key) ? [{ ...group, permissions }] : [];
    });
  }, [groups, debouncedSearch, showFilter, filter, selection, draft, pinnedGroups]);

  /**
   * The catalogue is ~230 rows on the entity plane. Rows are only mounted once
   * their group has been opened, so the initial paint is 20 headers rather than
   * every row in the palette.
   */
  const mountedGroups = useRef(new Set<string>());
  openGroups.forEach((key) => mountedGroups.current.add(key));

  // A search is only useful if it opens what it matched.
  useEffect(() => {
    if (!debouncedSearch.trim()) return;

    setOpenGroups(visibleGroups.map((group) => group.key));
  }, [debouncedSearch, visibleGroups]);

  const emptyMessage =
    showFilter && filter === PERMISSION_FILTERS.granted && !debouncedSearch.trim()
      ? NOTHING_GRANTED_MESSAGE
      : NO_MATCHES_MESSAGE;

  return (
    <PickerRoot>
      <PickerToolbar>
        <SearchField>
          <Input
            size="sm"
            value={search}
            placeholder="Search permissions"
            startIcon={<SearchIcon />}
            aria-label="Search permissions"
            onChange={(event) => setSearch(event.target.value)}
          />
        </SearchField>

        {showFilter ? (
          <Tabs.Root
            variant="boxed"
            size="sm"
            value={filter}
            onValueChange={(value) => setFilter(value as PermissionFilter)}
          >
            <Tabs.List>
              <Tabs.Tab value={PERMISSION_FILTERS.granted} label="Granted" counter={grantedCount} />
              <Tabs.Tab value={PERMISSION_FILTERS.all} label="All" />
            </Tabs.List>
          </Tabs.Root>
        ) : (
          summary && (
            <Typography variant="body4" color={theme.colors.text.tertiary}>
              {summary}
            </Typography>
          )
        )}
      </PickerToolbar>

      {visibleGroups.length === 0 ? (
        <EmptyState>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {emptyMessage}
          </Typography>
        </EmptyState>
      ) : (
        <GroupList>
          <Accordion
            multiple
            value={openGroups}
            onValueChange={(value) => setOpenGroups(value as string[])}
          >
            {visibleGroups.map((group) => {
              const fullGroup = groups.find(({ key }) => key === group.key) ?? group;
              const granted = countSelectedInGroup(fullGroup, selection);
              const total = fullGroup.permissions.length;

              return (
                <Accordion.Item
                  key={group.key}
                  value={group.key}
                  label={
                    <GroupLabel>
                      <Typography variant="body3" color={theme.colors.text.primary}>
                        {group.label}
                      </Typography>
                      <GroupLabelMeta>
                        <GroupMeter aria-hidden>
                          <Progress
                            value={granted}
                            max={Math.max(total, 1)}
                            size="sm"
                            direction="horizontal"
                          />
                        </GroupMeter>
                        <GroupCount>
                          <Typography
                            variant="body4"
                            color={theme.colors.text.tertiary}
                            aria-label={`${granted} of ${total} granted`}
                          >
                            {granted}/{total}
                          </Typography>
                        </GroupCount>
                      </GroupLabelMeta>
                    </GroupLabel>
                  }
                >
                  {mountedGroups.current.has(group.key) && (
                    <>
                      {renderGroupIntro?.(group)}

                      {editable && onBulkChange && (
                        <GroupBulkActions
                          group={fullGroup}
                          selection={selection}
                          canGrantProtected={canGrantProtected}
                          addBlocked={bulkAddBlockedGroups?.has(group.key)}
                          onApply={(add, remove) => onBulkChange(fullGroup, add, remove)}
                        />
                      )}

                      <PermissionRows>
                        {group.permissions.map((permission) => (
                          <PermissionRow
                            key={permission.permission}
                            permission={permission}
                            selection={selection.get(permission.permission)}
                            isGrantable={isPermissionGrantable(
                              permission.permission,
                              canGrantProtected,
                            )}
                            editable={editable}
                            change={draft?.get(permission.permission)}
                            pending={pending?.get(permission.permission)}
                            onChange={onChange}
                          />
                        ))}
                      </PermissionRows>
                    </>
                  )}
                </Accordion.Item>
              );
            })}
          </Accordion>
        </GroupList>
      )}
    </PickerRoot>
  );
};
